import { config } from '@cfg';
import { TypeMlsComplianceDataSkeleton } from '@contentful/types/TypeMlsComplianceData';
import { client } from '@data/contentfulClient';
import { DEFAULT_EXCHANGE_RATES } from '@util/constants';
import getURL, { extractDomain, getBaseUrlAndSubdomain, SubdomainInfo } from '@util/getURL';
import { apiClient } from 'api-client';
import { Entry } from 'contentful';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './root-store';
import { AbstractGeoResponse } from '@util/types';

export type OSNComplianceData = Entry<
  TypeMlsComplianceDataSkeleton,
  'WITHOUT_UNRESOLVABLE_LINKS'
>['fields'];
export type MlsComplianceData = Record<string, OSNComplianceData>;
export type MeasurementUnit = 'sq.ft' | 'm²';

/** Contains data used throughout the application such as translations,currency data, subdomain data, etc. */
export class AppStore {
  public geoInfo: AbstractGeoResponse | null = null;
  public exchangeRates: Record<string, number> | null = null;
  public mlsComplianceData: MlsComplianceData | null = null;
  public preferredCurrency: string | null = null;
  public preferredMeasurement: MeasurementUnit = 'sq.ft';
  public validSubdomainData: SubdomainInfo | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  async getUserPreferenceCookies() {
    this.preferredCurrency = (await getCookie('userPreferredCurrency')) || null;
    this.preferredMeasurement = ((await getCookie('userPreferredMeasurement')) ||
      'sq.ft') as MeasurementUnit;
  }

  /**
   * Returns the shop domain of an advisor or team when on an advisor/team subdomain.
   */
  get urlDomainOverride(): string | null {
    if (!this.validSubdomainData) {
      return null;
    }

    const subdomainType = this.validSubdomainData.type?.toLowerCase();
    const shopSubdomain = this.validSubdomainData.shopSubdomain;
    if (subdomainType && shopSubdomain && ['advisor', 'team'].includes(subdomainType)) {
      const currentDomain = extractDomain(window.location.host, shopSubdomain);
      return `${window.location.protocol}//${shopSubdomain}.${currentDomain}`;
    }

    return null;
  }

  setGeoInfo = (geoInfo: AbstractGeoResponse | null): void => {
    this.geoInfo = geoInfo;
  };

  setPreferredCurrency = (currency: string | null): void => {
    this.preferredCurrency = currency;

    const domain = extractDomain(getURL('', config), undefined, true);
    if (currency) {
      setCookie('userPreferredCurrency', currency, { domain });
    } else {
      deleteCookie('userPreferredCurrency', { domain });
    }
  };

  setPreferredMeasurement = (measurement: MeasurementUnit): void => {
    this.preferredMeasurement = measurement;

    const domain = extractDomain(getURL('', config), undefined, true);
    setCookie('userPreferredMeasurement', this.preferredMeasurement, { domain });
  };

  async getCurrencyConversions(): Promise<void> {
    try {
      const res = await apiClient.getCurrencyConversions();
      runInAction(() => {
        this.exchangeRates = res.data.conversions;
      });
    } catch (err) {
      console.error('Error fetching currency conversions', err);
      runInAction(() => {
        this.exchangeRates = DEFAULT_EXCHANGE_RATES;
      });
    }
  }

  async getMlsComplianceData(): Promise<void> {
    const mlsComplianceData = await client().getEntries<TypeMlsComplianceDataSkeleton>({
      content_type: 'mlsComplianceData',
      limit: 1000,
    });

    const modifiedData: MlsComplianceData = {};
    if (mlsComplianceData && mlsComplianceData.items?.length) {
      mlsComplianceData.items.map((item) => {
        if (item?.fields?.osn) {
          modifiedData[item.fields.osn] = item.fields;
        }
      });
    }
    runInAction(() => {
      this.mlsComplianceData = modifiedData;
    });
  }

  async getSubdomainData(): Promise<void> {
    if (typeof window === 'undefined') return;

    try {
      const subdomainInfo = await getBaseUrlAndSubdomain(window.location.host);
      runInAction(() => {
        this.validSubdomainData = subdomainInfo;
      });
    } catch (err) {
      console.error('Error fetching subdomain data', err);
    }
  }
}
